import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowDown } from 'lucide-react';
import '../styles/noisefast.css';

const NoiseFastPage = () => {
  // TEST link redirects to preview URL
  // PRODUCTION link should redirect to www.gocalfit.com/noise-fast/start
  const STRIPE_PAYMENT_LINK = "https://buy.stripe.com/test_6oUdRa2o38T59gSfVYfYY02";
  
  const handleCheckout = () => {
    window.open(STRIPE_PAYMENT_LINK, '_self');
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="nf-page">
      {/* Minimal Header */}
      <header className="nf-header">
        <Link to="/" className="nf-logo">GoCalFit</Link>
        <span className="nf-nav-marker">[ noise fast ]</span>
      </header>

      {/* Hero Section */}
      <section className="nf-hero" id="hero">
        <div className="nf-hero-bg"></div>
        <div className="nf-hero-content">
          <span className="nf-section-num">[ 01 ]</span>
          
          <h1 className="nf-headline">
            What's quietly draining<br />your attention?
          </h1>
          
          <p className="nf-subheadline">
            Most of it takes less than 10 minutes to start.
          </p>
          
          <div className="nf-hero-list">
            <p>The cluttered corner.</p>
            <p>The pile you keep avoiding.</p>
            <p>The subscription you still haven't canceled.</p>
            <p>The appointment you need to make.</p>
            <p>The thing quietly taking up way more energy than it should.</p>
          </div>
          
          <div className="nf-core-message">
            <p className="nf-dim">You don't need more motivation.</p>
            <p className="nf-bold">You need fewer things draining your attention.</p>
          </div>
          
          <div className="nf-noise-cut">
            <p>For 3 days, we cut the noise:</p>
            <ul>
              <li>no social media</li>
              <li>no podcasts</li>
              <li>no background input</li>
            </ul>
            <p className="nf-emphasis">So you can finally handle what's been quietly draining you.</p>
          </div>
          
          <button className="nf-cta" onClick={handleCheckout} data-testid="nf-hero-cta">
            Start Here — $27 <ArrowRight size={20} />
          </button>
          
          <button className="nf-scroll-hint" onClick={() => scrollToSection('how')}>
            <ArrowDown size={24} />
          </button>
        </div>
      </section>

      {/* How It Works */}
      <section className="nf-section nf-how" id="how">
        <div className="nf-section-inner">
          <span className="nf-section-num">[ 02 ]</span>
          
          <h2>
            We don't add more.<br />
            <span className="nf-highlight">We remove what's in the way.</span>
          </h2>
          
          <div className="nf-days">
            <div className="nf-day">
              <span className="nf-day-num">01</span>
              <h3>Cut the noise</h3>
              <p>So you stop escaping into distraction</p>
            </div>
            <div className="nf-day">
              <span className="nf-day-num">02</span>
              <h3>Sit in it</h3>
              <p>So you notice what's actually draining you</p>
            </div>
            <div className="nf-day">
              <span className="nf-day-num">03</span>
              <h3>Move. Handle it.</h3>
              <p>So you finally close one leak</p>
            </div>
          </div>
          
          <p className="nf-tagline">No overwhelm. No extra steps. No flexibility.</p>
        </div>
      </section>

      {/* The Rule */}
      <section className="nf-section nf-rule" id="rule">
        <div className="nf-section-inner">
          <span className="nf-section-num">[ 03 ]</span>
          
          <h2>Read this before you start</h2>
          
          <p className="nf-intro">This only works if you follow it.</p>
          
          <p className="nf-label">For 3 days:</p>
          
          <ul className="nf-rules">
            <li>No social media</li>
            <li>No podcasts</li>
            <li>No background noise</li>
            <li>No "just checking something"</li>
          </ul>
          
          <p className="nf-bold">No loopholes.</p>
          
          <div className="nf-deal">
            <p>If you follow it, it works.</p>
            <p>If you don't, it won't.</p>
            <p className="nf-highlight">That's the deal.</p>
          </div>
        </div>
      </section>

      {/* What You're Paying For */}
      <section className="nf-section nf-paying" id="paying">
        <div className="nf-section-inner">
          <span className="nf-section-num">[ 04 ]</span>
          
          <h2>What you're really paying for</h2>
          
          <p className="nf-dim">Not the steps.</p>
          <p className="nf-bold nf-large">Relief.</p>
          
          <p className="nf-label">Relief from:</p>
          
          <ul className="nf-relief-list">
            <li>mental clutter</li>
            <li>unfinished tasks</li>
            <li>low-level stress</li>
            <li>the energy drain of things left hanging</li>
          </ul>
          
          <p className="nf-emphasis">
            You're paying to stop carrying around things<br />
            that could be handled in minutes.
          </p>
        </div>
      </section>

      {/* What Changes */}
      <section className="nf-section nf-changes" id="changes">
        <div className="nf-section-inner">
          <span className="nf-section-num">[ 05 ]</span>
          
          <h2>What changes</h2>
          
          <p>That thing quietly draining your attention?</p>
          <p className="nf-bold">You handle it.</p>
          
          <div className="nf-stack">
            <p>Not perfectly.</p>
            <p>Not forever.</p>
            <p>Not all at once.</p>
          </div>
          
          <p className="nf-highlight">But enough to stop carrying it around in your head.</p>
          
          <div className="nf-shift">
            <p>And once you feel that shift…</p>
            <p className="nf-emphasis">you start seeing the other leaks too.</p>
          </div>
        </div>
      </section>

      {/* Incentive */}
      <section className="nf-section nf-incentive" id="incentive">
        <div className="nf-section-inner">
          <span className="nf-section-num">[ 06 ]</span>
          
          <h2>Actually finish it.</h2>
          
          <div className="nf-incentive-steps">
            <p>Complete all 3 days.</p>
            <p>Submit your final DONE.</p>
            <p className="nf-highlight">Get entered to win a free Personal Operations Session ($157 value).</p>
          </div>
          
          <p className="nf-tagline">Because follow-through should lead somewhere.</p>
          
          <button className="nf-cta" onClick={handleCheckout} data-testid="nf-incentive-cta">
            Start Here — $27 <ArrowRight size={20} />
          </button>
        </div>
      </section>

      {/* Soft Exit */}
      <section className="nf-section nf-exit">
        <div className="nf-section-inner nf-exit-inner">
          <p className="nf-dim">Not ready for this?</p>
          <p className="nf-dim">Need help with something specific right now?</p>
          <a 
            href="https://app.acuityscheduling.com/schedule.php?owner=37343334&appointmentType=85018998"
            target="_blank"
            rel="noopener noreferrer"
            className="nf-secondary-link"
          >
            Book a Personal Operations Session <ArrowRight size={16} />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="nf-footer">
        <Link to="/" className="nf-footer-logo">GoCalFit</Link>
        <p>Serious movement. Serious play.</p>
      </footer>
    </div>
  );
};

export default NoiseFastPage;
